/** Timeout for font load; after this we show the document anyway. */
export const FONT_LOAD_TIMEOUT_MS = 8000;

/** Extra delay after font load before showing the document so the browser can apply the font (avoids flash). */
export const FONT_APPLY_DELAY_MS = 500;

const FONT_ASSETS_ROOT = '/assets/fonts';

const HOSTED_STYLE_GUIDE_FONTS = {
  Tinos: {
    folder: 'tinos',
    cssFile: 'tinos.css',
    familyValue: '"Tinos", "Times New Roman", Times, serif',
    faces: [
      {weight: 400, style: 'normal', file: 'Tinos-Regular.ttf'},
      {weight: 700, style: 'normal', file: 'Tinos-Bold.ttf'},
      {weight: 400, style: 'italic', file: 'Tinos-Italic.ttf'},
      {weight: 700, style: 'italic', file: 'Tinos-BoldItalic.ttf'},
    ],
  },
  Selawik: {
    folder: 'selawik',
    cssFile: 'selawik.css',
    familyValue: '"Selawik", "Segoe UI", sans-serif',
    faces: [
      {weight: 400, style: 'normal', file: 'selawk.ttf'},
      {weight: 700, style: 'normal', file: 'selawkb.ttf'},
    ],
  },
  Carlito: {
    folder: 'carlito',
    cssFile: 'carlito.css',
    familyValue: '"Carlito", Calibri, "Segoe UI", sans-serif',
    faces: [
      {weight: 400, style: 'normal', file: 'Carlito-Regular.ttf'},
      {weight: 700, style: 'normal', file: 'Carlito-Bold.ttf'},
      {weight: 400, style: 'italic', file: 'Carlito-Italic.ttf'},
      {weight: 700, style: 'italic', file: 'Carlito-BoldItalic.ttf'},
    ],
  },
};

/**
 * Maps style-guide font family names to webfonts we load (self-hosted).
 * When the style guide specifies one of these, we inject the stylesheet and set
 * --document-font-family for consistent cross-OS layout.
 *
 * System fonts (e.g. Times New Roman, Segoe UI) are not listed here — the browser
 * uses the installed face with no webfont injection.
 *
 * Use the key `Tinos` explicitly when the org wants our hosted Tinos stack (not the default).
 */
export const STYLE_GUIDE_FONT_MAP = {
  ...Object.fromEntries(
    Object.entries(HOSTED_STYLE_GUIDE_FONTS).map(([fontFaceName, config]) => [
      fontFaceName,
      {
        href: `${FONT_ASSETS_ROOT}/${config.folder}/${config.cssFile}`,
        linkId: `document-font-${config.folder}`,
        familyValue: config.familyValue,
        fontFaceName,
      },
    ])
  ),
};

/** All injected stylesheet link IDs for hosted style-guide fonts. */
export const DOCUMENT_FONT_LINK_IDS = Object.values(STYLE_GUIDE_FONT_MAP).map(
  ({linkId}) => linkId
);

/**
 * Inline @font-face source definitions keyed by fontFaceName.
 * Used by injectPrintFonts to inject declarations directly into a print
 * iframe (the cloned <link> stylesheet may not be parsed in time).
 * Paths are absolute public paths so they resolve inside the iframe.
 */
export const FONT_FACES = {
  ...Object.fromEntries(
    Object.entries(HOSTED_STYLE_GUIDE_FONTS).map(([fontFaceName, config]) => [
      fontFaceName,
      config.faces.map((face) => ({
        weight: face.weight,
        style: face.style,
        src: `${FONT_ASSETS_ROOT}/${config.folder}/${face.file}`,
      })),
    ])
  ),
};
